import { Designer } from './Designer.js';
import { autofillSurface } from '../autoFill/index.js';
import type { CropType, IPhoto, IProject, ISurface } from '../common/index.js';
import { PhotobookPhotosSelector } from './photosSelector/PhotobookPhotosSelector.js';
import { isUserPhotoAsset } from '../common/utils/index.js';

/**
 * Designer for `photobook` projects.
 *
 * Distributes photos evenly across surfaces in rank-sorted batches so that
 * every spread receives a similarly high-quality set of photos. Within each
 * surface, `autofillSurface` assigns individual photos to containers using
 * the standard scoring formula (photo rank × 0.4 + crop rank × 0.4 +
 * orientation match × 0.2), placing the best-cropping photo in the largest
 * container first.
 *
 * Batch size is computed as `max(1, round(totalPhotos / totalSurfaces))`,
 * guaranteeing at least one photo per surface regardless of pool size.
 * Photos are consumed as they are placed and are not reused across surfaces.
 */
export class PhotobookDesigner extends Designer {

    private readonly photosSelector: PhotobookPhotosSelector;

    public constructor(project: IProject | undefined, photos: IPhoto[] | undefined, cropStrategy?: CropType) {
        super(project, photos, cropStrategy);
        this.photosSelector = new PhotobookPhotosSelector(photos ?? []);
    }

    public designProject(): IProject {
        const filledSurfaces: ISurface[] = [];
        const photoBatchSize = Math.max(1, Math.round(this.photos.length / this.project.surfaces.length));

        for (const surface of this.project.surfaces) {
            const batch = this.photosSelector.getNextBatch(photoBatchSize);

            const updatedSurface = autofillSurface(surface, batch, this.cropStrategy);

            filledSurfaces.push(updatedSurface);

            for (const asset of updatedSurface.assets) {
                if (isUserPhotoAsset(asset) && asset.content?.photoId) {
                    this.photosSelector.updatedPhotoSet(asset.content.photoId);
                }
            }
        }

        return {
            ...this.project,
            photos: this.photosSelector.getUsedPhotos(),
            surfaces: filledSurfaces,
        };
    }

}
